/**
 * PerformanceChart.jsx
 * Impressions and engagements, day by day.
 *
 * Two panels rather than two lines on one axis. Impressions run in the tens of
 * thousands and engagements in the hundreds, so drawing both against a single
 * scale would flatten engagements onto the baseline and quietly make the more
 * interesting of the two unreadable. Each panel keeps its own scale; they
 * share an x-axis, a crosshair and one tooltip, so they are still read
 * together.
 *
 * Every value the tooltip shows is also reachable without hovering, through
 * the figures table underneath.
 *
 * Dependencies: helpers.js, components/Icons.jsx
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Chart, Table } from '../Icons.jsx';
import { compact, full } from './helpers.js';

const PAD = { left: 46, right: 14, top: 10, bottom: 22 };
const PANEL = { height: 112, gap: 30, second: 64 };
const HEIGHT = PAD.top + PANEL.height + PANEL.gap + PANEL.second + PAD.bottom;

const SERIES = [
  { key: 'impressions', label: 'Impressions', slot: 1, height: PANEL.height, top: PAD.top },
  {
    key: 'engagements',
    label: 'Engagements',
    slot: 2,
    height: PANEL.second,
    top: PAD.top + PANEL.height + PANEL.gap,
  },
];

/** "3 Sep" — short enough for an axis, unambiguous enough to read. */
const axisDate = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

const longDate = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/** A rounded top for the scale, so the axis reads 20K rather than 19,438. */
function niceMax(value) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  return magnitude * (steps.find((s) => value <= magnitude * s) ?? 10);
}

/**
 * The window's totals, under the chart.
 *
 * Not a repeat of the series: the chart shows shape and these show size, and
 * "what did the last 30 days add up to" is a different question from "which
 * day was busy".
 */
function Totals({ current = {} }) {
  const cells = [
    { label: 'Total impressions', value: compact(current.impressions) },
    { label: 'Total engagements', value: compact(current.engagements) },
    {
      label: 'Engagement rate',
      value: current.rate === null || current.rate === undefined
        ? '—'
        : `${current.rate.toFixed(1)}%`,
    },
  ];

  return (
    <div className="viz-totals">
      {cells.map((c) => (
        <div key={c.label}>
          <div className="viz-totals__value">{c.value}</div>
          <div className="viz-totals__label">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function PerformanceChart({
  series = [],
  current,
  days,
  measuredAt,
  loading,
  scopeLabel = 'across every connected brand',
  filter = null,
}) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(560);
  const [active, setActive] = useState(null);
  const [showTable, setShowTable] = useState(false);

  // The chart draws at real pixel widths rather than being stretched from a
  // fixed viewBox — a scaled viewBox would stretch the strokes and the type
  // with it.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(320, Math.round(entry.contentRect.width)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // A window change can leave the crosshair pointing at a day that is no
  // longer in the data.
  useEffect(() => {
    setActive(null);
  }, [series]);

  const points = series.length;
  const plotWidth = Math.max(1, width - PAD.left - PAD.right);
  // One point would divide by zero; it is drawn as a dot in the middle.
  const x = (i) => PAD.left + (points < 2 ? plotWidth / 2 : (i / (points - 1)) * plotWidth);

  const scales = {};
  for (const s of SERIES) {
    const max = niceMax(Math.max(...series.map((d) => d[s.key] ?? 0), 0));
    scales[s.key] = { max, y: (v) => s.top + s.height - ((v ?? 0) / max) * s.height };
  }

  const path = (s, close = false) => {
    const line = series
      .map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${scales[s.key].y(d[s.key]).toFixed(1)}`)
      .join(' ');
    if (!close) return line;
    return `${line} L${x(points - 1).toFixed(1)} ${s.top + s.height} L${x(0).toFixed(1)} ${
      s.top + s.height
    } Z`;
  };

  /** The nearest day to the pointer — readers aim at a date, not at a 2px line. */
  function locate(event) {
    const box = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - box.left;
    if (points < 2) return 0;
    const i = Math.round(((px - PAD.left) / plotWidth) * (points - 1));
    return Math.min(points - 1, Math.max(0, i));
  }

  function onKeyDown(event) {
    if (!points) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      setActive((i) => Math.min(points - 1, Math.max(0, (i ?? 0) + step)));
    }
    if (event.key === 'Escape') setActive(null);
  }

  const hovered = active !== null ? series[active] : null;

  // Axis labels: first, middle and last, so a 90-day window does not print
  // ninety dates on top of each other.
  const ticks = points < 2 ? [0] : [0, Math.floor((points - 1) / 2), points - 1];

  return (
    <section className="card dash-chart">
      <div className="card__head">
        <span className="tile">
          <Chart size={21} />
        </span>
        <div className="card__head-text">
          <h2>Content performance</h2>
          <p>How published posts did, {scopeLabel}.</p>
        </div>
        <div className="card__head-end">
          <ul className="viz-legend">
            {SERIES.map((s) => (
              <li key={s.key}>
                <span className={`viz-legend__key viz-legend__key--${s.slot}`} />
                {s.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {filter}

      {points < 2 ? (
        <div className="empty empty--inline">
          <div className="empty__art">
            <Chart size={26} />
          </div>
          <h3>Not enough measurements yet</h3>
          <p>
            Figures are collected once a night for posts published in the last 30 days. The chart
            appears once there are two days to compare.
          </p>
        </div>
      ) : (
        <>
          <div className={`viz${loading ? ' is-loading' : ''}`} ref={wrapRef}>
            <svg
              width={width}
              height={HEIGHT}
              role="img"
              tabIndex={0}
              aria-label={`Impressions and engagements per day over the last ${days} days`}
              onPointerMove={(e) => setActive(locate(e))}
              onPointerLeave={() => setActive(null)}
              onKeyDown={onKeyDown}
              onBlur={() => setActive(null)}
            >
              {SERIES.map((s) => {
                const scale = scales[s.key];
                return (
                  <g key={s.key}>
                    {/* Three gridlines per panel: floor, middle, top. */}
                    {[0, 0.5, 1].map((f) => (
                      <line
                        key={f}
                        className="viz-grid"
                        x1={PAD.left}
                        x2={width - PAD.right}
                        y1={s.top + s.height - f * s.height}
                        y2={s.top + s.height - f * s.height}
                      />
                    ))}

                    {[0, 1].map((f) => (
                      <text
                        key={f}
                        className="viz-tick"
                        x={PAD.left - 8}
                        y={s.top + s.height - f * s.height + 4}
                        textAnchor="end"
                      >
                        {f === 0 ? '0' : compact(scale.max)}
                      </text>
                    ))}

                    {/* The panel names its own series, so identity never rests
                        on colour alone. */}
                    <text className="viz-panel-label" x={PAD.left} y={s.top - 6}>
                      {s.label}
                    </text>

                    <path className={`viz-area viz-area--${s.slot}`} d={path(s, true)} />
                    <path className={`viz-line viz-line--${s.slot}`} d={path(s)} />

                    {hovered && (
                      <circle
                        className={`viz-dot viz-dot--${s.slot}`}
                        cx={x(active)}
                        cy={scale.y(hovered[s.key])}
                        r="4.5"
                      />
                    )}
                  </g>
                );
              })}

              {hovered && (
                <line
                  className="viz-crosshair"
                  x1={x(active)}
                  x2={x(active)}
                  y1={PAD.top - 4}
                  y2={HEIGHT - PAD.bottom}
                />
              )}

              {ticks.map((i) => (
                <text
                  key={i}
                  className="viz-tick"
                  x={x(i)}
                  y={HEIGHT - 6}
                  textAnchor={i === 0 ? 'start' : i === points - 1 ? 'end' : 'middle'}
                >
                  {axisDate(series[i].day)}
                </text>
              ))}
            </svg>

            {hovered && (
              <div
                className="viz-tip"
                style={{
                  left: `${Math.min(Math.max(x(active), 76), width - 76)}px`,
                }}
              >
                <div className="viz-tip__day">{longDate(hovered.day)}</div>
                {SERIES.map((s) => (
                  <div className="viz-tip__row" key={s.key}>
                    <span className={`viz-legend__key viz-legend__key--${s.slot}`} />
                    <strong>{full(hovered[s.key])}</strong>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Totals current={current} />

          <div className="dash-chart__foot">
            <span className="row__sub">
              {measuredAt
                ? `Collected nightly. Last reading ${new Date(
                    measuredAt.endsWith('Z') ? measuredAt : `${measuredAt}Z`,
                  ).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}.`
                : 'Figures are collected once a night.'}
            </span>
            <button className="btn btn--sm" onClick={() => setShowTable((v) => !v)}>
              <Table size={15} /> {showTable ? 'Hide figures' : 'Show figures'}
            </button>
          </div>

          {showTable && (
            <div className="viz-table-wrap">
              <table className="viz-table">
                <thead>
                  <tr>
                    <th scope="col">Day</th>
                    <th scope="col">Impressions</th>
                    <th scope="col">Engagements</th>
                  </tr>
                </thead>
                <tbody>
                  {[...series].reverse().map((d) => (
                    <tr key={d.day}>
                      <th scope="row">{longDate(d.day)}</th>
                      <td>{full(d.impressions)}</td>
                      <td>{full(d.engagements)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
